import React, { useState } from 'react';

import { useMutation } from 'react-apollo';
import ReactGA from 'react-ga4';
import { useHistory, useLocation } from 'react-router-dom';

import * as actions from 'actions';
import Notice from 'Components-redesign/Notice';
import { Heading } from 'Components-redesign/Typography/Heading';
import { useDispatch, useI18n, useLogger } from 'Hooks';

import { PASSWORD_RESET_MUTATION } from './queries';
import {
  ErrorMessage,
  InnerWrapper,
  Input,
  InputWrapper,
  LabelText,
  SubmitButton,
  Text,
  VisibilityIcon,
  Wrapper,
} from './styles';

const minimumLength = process.env.NODE_ENV === 'development' ? 4 : 8;

const PasswordReset: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const logger = useLogger('reset-password');
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const passwordCheck = (password: string) => {
    const passwordRegEx = /^(?=.*[A-Z])(?=.*\d)[0-9A-Za-z!@#$%^&*?_~]+$/;
    const trimmedPassword = password.trim();
    let errorMessage = '';

    if (trimmedPassword.length < minimumLength) {
      errorMessage = t('ResetPassword.PasswordIsTooShort');
    } else if (!passwordRegEx.test(trimmedPassword)) {
      errorMessage = t('ResetPassword.PasswordMustContain');
    }

    return errorMessage;
  };

  const query = new URLSearchParams(useLocation().search);
  const token = query.get('token');

  const [resetPassword, { loading }] = useMutation(PASSWORD_RESET_MUTATION, {
    onCompleted: (data) => {
      if (data.passwordResetConfirm.success) {
        logger.info('Password has been changed', { token });
        ReactGA.event({
          category: 'Change password',
          action: 'Password_has_been_changed',
        });
        Promise.resolve(1)
          .then(() =>
            dispatch(actions.showNotice(t('ResetPassword.PasswordSuccessfullyUpdated'), 3000)),
          )
          .then(() => setTimeout(() => history.push('/'), 3000));
      } else {
        logger.error('Failed to change password', { message: data.passwordResetConfirm.message });
        setErrorMessage(data.passwordResetConfirm.message);
      }
    },
    onError: (error) => {
      logger.error('Failed to change password', { message: error.message });
      setErrorMessage(error.message);
    },
  });

  const handleChange = (e) => {
    if (errorMessage.length > 0) {
      setErrorMessage('');
    }
    setPassword(e.target.value);
  };

  const handleSubmit = () => {
    const passwordCheckErrorMessage = passwordCheck(password);
    if (passwordCheckErrorMessage.length > 0) {
      setErrorMessage(passwordCheckErrorMessage);
    } else {
      resetPassword({ variables: { token, newPassword: password } });
    }
  };

  return (
    <>
      <Wrapper>
        <InnerWrapper>
          <Heading level={2}>{t('ResetPassword.ResetYourPassword')}</Heading>
          <Text>{t('ResetPassword.UseEightOrMoreCharacters')}</Text>
          <LabelText>{t('ResetPassword.NewPassword')}</LabelText>
          <InputWrapper>
            <Input
              name="password"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              value={password}
              onChange={handleChange}
              type={visible ? 'text' : 'password'}
              style={errorMessage.length > 0 ? { borderColor: 'var(--colorUIRed)' } : {}}
            />
            <VisibilityIcon isVisible={visible} onClick={() => setVisible(!visible)} />
          </InputWrapper>
          <ErrorMessage>{errorMessage}</ErrorMessage>
          <SubmitButton type="submit" isLoading={loading} onClick={handleSubmit}>
            {loading ? (
              <>{t('General.LoadingThreeDots')}</>
            ) : (
              <span>{t('ResetPassword.Reset')}</span>
            )}
          </SubmitButton>
        </InnerWrapper>
      </Wrapper>

      <Notice color={'#A6D6B5'} />
    </>
  );
};

export default PasswordReset;
